import React, { useState } from 'react';
import { Layout, Typography, Alert, notification, Select, Space } from 'antd';
import SearchForm from './components/SearchForm';
import CandleStickChart from './components/CandleStickChart';
import { Candle, SearchParams } from './types';
import { fetchCandleData } from './services/cryptoService';
import './App.css';
import { useI18n } from './i18n/I18nProvider';
import { LANGUAGE_LABELS, type Language } from './i18n/types';

const { Header, Content, Footer } = Layout;
const { Title } = Typography;

const App: React.FC = () => {
  const { lang, setLang, t } = useI18n();
  const [candles, setCandles] = useState<Candle[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  // 搜索加密货币数据
  const handleSearch = async (params: SearchParams) => {
    setLoading(true);
    setError(null);
    
    try {
      // 获取K线数据
      const candleData = await fetchCandleData(params);
      setCandles(candleData);
      setHasSearched(true);
      
      // 显示加载完成通知
      notification.success({
        message: t('search.loadDoneTitle'),
        description: t('search.loadDoneDesc', { count: candleData.length }),
        duration: 3,
      });
    } catch (err: any) {
      setError(err.message || t('common.error'));
      setCandles([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout className="app-layout">
      <Header className="app-header">
        <div className="header-inner">
          <div className="header-brand">
            <Title level={3} className="app-title">{t('app.title')}</Title>
            <div className="app-subtitle">{t('app.subtitle')}</div>
          </div>

          <Space size={10} className="header-actions">
            <span className="lang-label">{t('lang.label')}</span>
            <Select
              value={lang}
              onChange={(v) => setLang(v as Language)}
              className="lang-select"
              options={(Object.keys(LANGUAGE_LABELS) as Language[]).map((k) => ({
                value: k,
                label: LANGUAGE_LABELS[k],
              }))}
            />
          </Space>
        </div>
      </Header>
      
      <Content className="app-content">
        <SearchForm onSearch={handleSearch} loading={loading} />
        
        {error && (
          <Alert
            message={t('common.error')}
            description={error}
            type="error"
            closable
            className="error-alert"
          />
        )}
        
        {hasSearched && candles.length > 0 && (
          <CandleStickChart candles={candles} patterns={[]} />
        )}
        
        {hasSearched && candles.length === 0 && !error && (
          <Alert
            message={t('search.noDataTitle')}
            description={t('search.noDataDesc')}
            type="info"
            className="info-alert"
          />
        )}
      </Content>
      
      <Footer className="app-footer">
        <p>{t('app.title')} ©{new Date().getFullYear()}</p>
        <p className="disclaimer">{t('app.disclaimer')}</p>
      </Footer>
    </Layout>
  );
};

export default App;
